import { html } from '@adobe/edex/ui/edex-lit';
import { ProductSchema } from '@adobe/edex/ui/types';
import { I18nKey } from '@adobe/edex/ui/shared/constants/i18n';
import { $t } from '@adobe/edex/ui/shared/config/i18n';
import { BaseConnectedElement } from '@adobe/edex/ui/components/base.element';
import { getProductIconURL } from '@adobe/edex/ui/shared/utils';
import { getSearchByProductUrl } from '@adobe/edex/ui/shared/utils/routes';
import { SearchEntityType } from '@adobe/edex/ui/shared/types/search';
import { AnalyticsEvent, CHECK_ENTITLEMENTS, productToEventName, prodToFI } from '@adobe/edex/ui/constants';
import { Analytics } from '@adobe/edex/ui/shared/config/analytics';
import { EdexAuth } from '@adobe/edex/ui/shared/config/auth';
import { entitlementsAPI } from '@adobe/edex/ui/api';

export class ToolBase extends BaseConnectedElement {
  static get properties() {
    return {
      resourceID: { type: String },
    };
  }

  resourceID: string;

  renderProductTitle = (product: ProductSchema) =>
    html`
      <div class="product-title">${product.title}</div>
    `;

  renderProductLogo = (product: ProductSchema) => {
    return html`
      <sp-link
        href="${getSearchByProductUrl(product, SearchEntityType.resources)}"
        class="product-logo"
        target="_blank"
      >
        <img src=${getProductIconURL(product)} />
      </sp-link>
    `;
  };

  login = async (product: ProductSchema, url, eventName, resourceId, event) => {
    event.preventDefault();
    this.trackToolLinkClick(product, url, eventName, resourceId);
    await EdexAuth.signIn();
  };
  trackToolLinkClick = (product: ProductSchema, url: string, event: AnalyticsEvent, id: string) => {
    const { title } = product;
    this.track({
      event,
      payload: {
        id,
        url,
        title,
      },
    });
    Analytics.getInstance().emitEvent(event, title);
  };

  getMessage = async (product: ProductSchema) => {
    let message = $t(I18nKey.getFreeTrial);
    let link = product.links?.try;
    const onclick = this.trackToolLinkClick;
    let eventName = AnalyticsEvent.getFreeTrial;
    if (CHECK_ENTITLEMENTS.indexOf(product.vanityURL) === -1) {
      return { message, link, onclick, eventName, openInNewTab: true, resourceID: this.resourceID };
    }

    const isSignedIn = (await window.adobeIMS?.isSignedInUser()) || false;
    if (!isSignedIn) {
      return {
        message: $t(I18nKey.signInCheckAccess),
        link: '#',
        onclick: this.login,
        eventName: AnalyticsEvent.toolsLogin,
        openInNewTab: false,
        resourceID: this.resourceID,
      };
    }

    // CCEDU-4171
    const userType = (await window.adobeIMS.getProfile())?.account_type;
    if (userType.startsWith('type1')) {
      message = $t(I18nKey.createNewProject);
      link = product.links?.support;
      eventName = productToEventName[product.vanityURL].createNewProject;
    } else if (userType?.startsWith('type2') || userType?.startsWith('type3')) {
      // for the response, see https://wiki.corp.adobe.com/pages/viewpage.action?pageId=2503017162
      // const entitlements = await entitlementsAPI.get<{ entitlements: any[] }>('/');
      // const isEntitled = await this.callIsEntitled(product.vanityURL);
      const entitlements = await entitlementsAPI.get<{ entitlements: any[] }>('/');
      if (this.isEntitled(entitlements.data, product.vanityURL)) {
        message = $t(I18nKey.createNewProject);
        // The support attribute contains the launch link for ccx, may need better attribute.
        link = product.links?.support;
        eventName = productToEventName[product.vanityURL].createNewProject;
      } else {
        message = $t(I18nKey.getAccess);
        link = product.links?.buy;
        eventName = productToEventName[product.vanityURL].buy;
      }
    }
    return { message, link, onclick, eventName, openInNewTab: true, resourceID: this.resourceID };
  };

  // callIsEntitled(product: string) {
  //   return isEntitled(product);
  // }

  isEntitled = (entitlements: any, product: string) => {
    if (!entitlements || !entitlements?.entitlements) {
      return false;
    }
    // see sample response:
    // https://wiki.corp.adobe.com/pages/viewpage.action?pageId=2503017162#EMSGetEntitlementsAPISpecification-SampleResponse
    /*
    Varahamurthy Jutur  :
    User is considered not entitled in all the following cases
      FI missing
      FI present but disabled
      FI present and enabled but it is part of a license/entitlement whose status is not ACTIVE
    In other words, if an FI is enabled in an ACTIVE entitlement, 
    user is considered entitled to use the value provided by that FI, otherwise not
 */
    for (const e of entitlements?.entitlements) {
      if (e.status !== 'ACTIVE') {
        continue;
      }
      const prodKey = prodToFI[product] || product;
      const prod = e.fulfillable_items?.[prodKey];
      if (!prod) {
        continue;
      }
      return prod.enabled === true;
    }
    return false;
  };

  renderTryLink = async (product: ProductSchema) => {
    const { message, link, onclick, eventName, openInNewTab, resourceID } = await this.getMessage(product);
    return html`
      <sp-link
        class="product-try-link"
        href="${link}"
        @click="${(event) => onclick(product, link, eventName, resourceID, event)}"
        target="${openInNewTab ? '_blank' : '_top'}"
        quiet
      >
        ${message}
      </sp-link>
    `;
  };
}
